import { useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth, ROLES } from '@/context/AuthContext';
import { useWebSocket } from '@/context/WebSocketContext';
import { PushBanner } from '@/components/PushBanner';
import { useTheme } from '@/context/ThemeContext';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import {
  LayoutDashboard,
  Cpu,
  AlertTriangle,
  Building2,
  DoorOpen,
  Sliders,
  Bell,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronRight,
  Shield,
  Radio,
  BarChart3,
  Sun,
  Moon,
  BellRing,
  Users,
  Wind,
  BookOpen,
  FileText,
  FolderDown,
  Upload,
  Clock,
  Library,
  UserCheck,
  FlaskConical,
  Briefcase,
  Heart,
  Flame,
  GraduationCap
} from 'lucide-react';

// Define nav items with role requirements
const NAV_ITEMS = [
  { path: '/', label: 'Översikt', icon: LayoutDashboard },
  { path: '/kommun', label: 'Kommunöversikt', icon: Building2, kommunOnly: true },
  { path: '/devices', label: 'Enheter', icon: Cpu },
  { path: '/events', label: 'Händelser', icon: AlertTriangle },
  { path: '/alarm-history', label: 'Larmhistorik', icon: Clock },
  { path: '/incidents', label: 'Ärenden', icon: FileText },
  { path: '/air-quality', label: 'Luftkvalitet', icon: Wind },
  { path: '/notifications', label: 'Notifikationer', icon: BellRing },
  { path: '/analytics', label: 'Analys', icon: BarChart3 },
  { path: '/schools', label: 'Skolor', icon: Building2, superadminOnly: true },
  { path: '/rooms', label: 'Rum', icon: DoorOpen, superadminOnly: true },
  { path: '/users', label: 'Användare', icon: Users, schoolAdminOrSuper: true },
  { path: '/alarm-profiles', label: 'Larmprofiler', icon: Sliders, superadminOnly: true },
  { path: '/notification-rules', label: 'Aviseringar', icon: Bell, superadminOnly: true },
  { path: '/settings', label: 'Inställningar', icon: Settings, superadminOnly: true },
  { path: '/academy', label: 'Vapelarm Academy', icon: GraduationCap },
  { path: '/knowledge', label: 'Kunskapsbank', icon: Library },
  { path: '/import', label: 'Importera sensorer', icon: Upload, superadminOnly: true },
  { path: '/onboarding', label: 'Onboarding-guide', icon: BookOpen, superadminOnly: true },
  { path: '/admin-guide', label: 'Ny kund-guide', icon: Shield, superadminOnly: true },
  { path: '/onboarding-submissions', label: 'Kundregistrering', icon: UserCheck, superadminOnly: true },
  { path: '/sensor-linking', label: 'Sensorkoppling', icon: Radio, superadminOnly: true },
  { path: '/unlinked-sensors', label: 'Okopplade sensorer', icon: Cpu, superadminOnly: true },
  { path: '/test-lab', label: 'Testlabb', icon: FlaskConical, superadminOnly: true },
  { path: '/sales', label: 'Säljportal', icon: Briefcase, superadminOnly: true },
  { path: '/reports', label: 'Rapporter', icon: Heart, adminOnly: true },
  { path: '/leads', label: 'Vapelarm Leads', icon: Flame, superadminOnly: true },
];

// Role badge colors
const getRoleBadge = (role) => {
  switch (role) {
    case ROLES.SUPERADMIN:
      return { label: 'Superadmin', className: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' };
    case ROLES.KOMMUN_ADMIN:
      return { label: 'Kommunadmin', className: 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200' };
    case ROLES.SCHOOL_ADMIN:
      return { label: 'Skoladmin', className: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' };
    case ROLES.STAFF:
      return { label: 'Personal', className: 'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-200' };
    default:
      return { label: role || 'Användare', className: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300' };
  }
};

// Live indicator component
function LiveIndicator() {
  const { connected } = useWebSocket();
  
  return (
    <div className="flex items-center gap-1.5 text-xs">
      <span className={cn(
        "w-2 h-2 rounded-full",
        connected ? "bg-green-500 animate-pulse" : "bg-slate-400 dark:bg-slate-600"
      )} />
      <span className={cn(
        "font-ibm",
        connected ? "text-green-700 dark:text-green-400" : "text-slate-500 dark:text-slate-400"
      )}>
        {connected ? "LIVE" : "Offline"}
      </span>
    </div>
  );
}

// Theme toggle component
function ThemeToggle() {
  const { isDark, toggleTheme } = useTheme();
  
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={toggleTheme}
      className="h-8 w-8 p-0"
      title={isDark ? 'Växla till ljust läge' : 'Växla till mörkt läge'}
    >
      {isDark ? (
        <Sun className="h-4 w-4 text-amber-400" />
      ) : (
        <Moon className="h-4 w-4 text-slate-600" />
      )}
    </Button>
  );
}

export default function MainLayout() {
  const { user, logout, canCreate, isSuperAdmin, isKommunAdmin, isStaff } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { unreadCount, clearUnread } = useWebSocket();

  // Staff gets a full-screen alarm view — no sidebar
  if (isStaff()) {
    return <Outlet />;
  }

  // Kommun admin redirect to kommun dashboard on index
  const isKommunUser = isKommunAdmin();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleNotificationClick = () => {
    clearUnread();
    navigate('/notifications');
    setSidebarOpen(false);
  };

  const isAdmin = canCreate();
  const isSuperAdminUser = isSuperAdmin();
  const roleBadge = getRoleBadge(user?.role);

  // Filter nav items based on role
  const visibleNavItems = NAV_ITEMS.filter(item => {
    if (item.kommunOnly) return isKommunUser || isSuperAdminUser;
    if (item.schoolAdminOrSuper) return isSuperAdminUser || user?.role === 'school_admin';
    if (item.superadminOnly) return isSuperAdminUser;
    if (item.adminOnly) return isAdmin;
    return true;
  });

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 transition-colors">
      {/* Mobile header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="font-chivo font-black text-lg text-navy dark:text-white">Vapelarm Cloud</h1>
          <LiveIndicator />
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleNotificationClick}
            className="relative p-1.5 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
            data-testid="notification-bell-mobile"
          >
            <BellRing className={cn("h-5 w-5", unreadCount > 0 ? "text-red-500" : "text-slate-500 dark:text-slate-400")} />
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-600 text-white text-[10px] font-bold px-1">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>
          <ThemeToggle />
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            data-testid="mobile-menu-btn"
          >
            {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>
      </div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 z-40 bg-black/50"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={cn(
        "fixed top-0 left-0 z-40 h-screen w-64 bg-white dark:bg-slate-800 border-r border-slate-200 dark:border-slate-700 flex flex-col transition-transform duration-300",
        "lg:translate-x-0",
        sidebarOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        {/* Logo */}
        <div className="px-6 py-5 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center justify-between">
            <h1 className="font-chivo font-black text-xl text-navy dark:text-white tracking-tight">
              Vapelarm Cloud
            </h1>
            <LiveIndicator />
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 font-ibm uppercase tracking-widest">
            SaaS-plattform
          </p>
        </div>

        {/* Navigation */}
        <ScrollArea className="flex-1 py-4">
          <nav className="px-3 space-y-1">
            {visibleNavItems.map((item) => {
              const isActive = location.pathname === item.path || 
                (item.path !== '/' && location.pathname.startsWith(item.path));
              const Icon = item.icon;
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-ibm font-medium transition-colors",
                    isActive 
                      ? "bg-navy dark:bg-slate-700 text-white" 
                      : "text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-900 dark:hover:text-white"
                  )}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  <Icon className="h-4 w-4" />
                  {item.label}
                  {item.path === '/notifications' && unreadCount > 0 && (
                    <span className="ml-auto min-w-[20px] h-[20px] flex items-center justify-center rounded-full bg-red-600 text-white text-[10px] font-bold px-1">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                  {isActive && item.path !== '/notifications' && <ChevronRight className="h-4 w-4 ml-auto" />}
                </Link>
              );
            })}
          </nav>
        </ScrollArea>

        {/* User section */}
        <div className="p-4 border-t border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-3 px-3 py-2">
            <div className="h-8 w-8 rounded-full bg-navy dark:bg-slate-600 text-white flex items-center justify-center font-ibm font-semibold text-sm">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 dark:text-white truncate font-ibm">
                {user?.name || 'User'}
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                {user?.email}
              </p>
            </div>
            <ThemeToggle />
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              className="text-slate-500 hover:text-red-600"
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
          {/* Role badge */}
          <div className="px-3 mt-2">
            <Badge variant="secondary" className={cn("text-xs font-ibm", roleBadge.className)}>
              <Shield className="h-3 w-3 mr-1" />
              {roleBadge.label}
            </Badge>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="lg:ml-64 min-h-screen dark:bg-slate-900">
        <div className="pt-16 lg:pt-0">
          <PushBanner />
          <Outlet />
        </div>
      </main>
    </div>
  );
}
